import { useCallback, useState } from 'react';
import * as roleService from '../services/roleService';
import { useUIStore } from '../stores/uiStore';
import { IRole, IRoleMutationRequest } from '../types/interfaces/IRole';
import { IRoleTemplate } from '../types/interfaces/IRoleTemplate';
import { ApiResponse } from '../services/types';
import { notifyApiError } from '../services/core/apiErrorUX';
import { notifyApiSuccess } from '../services/core/apiSuccessUX';

// Hook específico para roles
export function useRoles() {
  const [loading, setLoading] = useState(false);
  const { addNotification } = useUIStore();

  const getAllRoles = useCallback(async (institutionId?: string): Promise<IRole[] | null> => {
    setLoading(true);
    try {
      const response = await roleService.getRoles(institutionId);

      if (response.success && response.data) {
        return response.data; // Retornar data directa
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al cargar roles',
        });
        return null;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al cargar roles',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  const getRoleTemplates = async (): Promise<IRoleTemplate[] | null> => {
    setLoading(true);
    try {
      const response = await roleService.getRoleTemplates();

      if (response.success && response.data) {
        return response.data;
      }

      notifyApiError(addNotification, response, {
        context: 'module',
        fallbackMessage: 'Error al cargar plantillas de roles',
      });
      return null;
    } catch {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al cargar plantillas de roles',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getRoleById = async (
    id: string,
    institutionId?: string
  ): Promise<IRole | null> => {
    setLoading(true);
    try {
      const response = await roleService.getRoleById(id, institutionId);

      if (response.success && response.data) {
        return response.data; // Retornar data directa
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al cargar rol',
        });
        return null;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al cargar rol',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createRole = async (
    roleData: IRoleMutationRequest,
    institutionId?: string
  ): Promise<ApiResponse<IRole>> => {
    setLoading(true);
    try {
      const response = await roleService.createRole(roleData, institutionId);

      if (response.success && response.data) {
          notifyApiSuccess(addNotification, 'Rol Creado', 'Rol creado correctamente');
        return response;
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al crear rol',
        });
        return response;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Error al crear rol';
      addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      });
      return {
        success: false,
        data: null as unknown as IRole,
        error: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async (
    id: string,
    roleData: IRoleMutationRequest,
    institutionId?: string
  ): Promise<ApiResponse<IRole>> => {
    setLoading(true);
    try {
      const response = await roleService.updateRole(
        id,
        roleData,
        institutionId
      );

      if (response.success && response.data) {
        notifyApiSuccess(addNotification, 'Rol Actualizado', 'Rol actualizado correctamente');
        return response;
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al actualizar rol',
        });
        return response;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Error al actualizar rol';
      addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      });
      return {
        success: false,
        data: null as unknown as IRole,
        error: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

  const deleteRole = async (
    id: string,
    institutionId?: string
  ): Promise<ApiResponse<boolean>> => {
    setLoading(true);
    try {
      const response = await roleService.deleteRole(id, institutionId);

      if (response.success) {
        notifyApiSuccess(addNotification, 'Rol Eliminado', 'Rol eliminado correctamente');
        return response;
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al eliminar rol',
        });
        return response;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Error al eliminar rol';
      addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      });
      return {
        success: false,
        data: false,
        error: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

  const toggleRoleStatus = async (
    id: string,
    institutionId?: string
  ): Promise<ApiResponse<IRole>> => {
    setLoading(true);
    try {
      const response = await roleService.toggleRoleStatus(id, institutionId);

      if (response.success && response.data) {
        notifyApiSuccess(addNotification, 'Estado Cambiado', 'Estado de rol cambiado correctamente');
        return response;
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al cambiar estado',
        });
        return response;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Error al cambiar estado';
      addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      });
      return {
        success: false,
        data: null as unknown as IRole,
        error: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    getAllRoles, // Retorna IRole[] o null
    getRoleTemplates, // Retorna IRoleTemplate[] o null
    getRoleById, // Retorna IRole o null
    createRole, // Retorna IRole o null
    updateRole, // Retorna IRole o null
    deleteRole, // Retorna boolean
    toggleRoleStatus, // Retorna IRole o null
    loading,
  };
}
